import axios from "axios";
import { entitySubtypesCache } from "./localCache";
import { createDevConsole } from "../utils/devLogger";
import { countUnseenBaselineRejections } from "../v2/utils/baselineApprovalSubmitterSeen";

const console = createDevConsole();

// Production URL: set REACT_APP_API_URL at build time, or deploy-all.ps1 replaces localhost:8000 in build output
export const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || "http://localhost:8000")
    : "http://localhost:8000";

// Configure axios defaults
axios.defaults.baseURL = API_BASE_URL;
axios.defaults.timeout = 600000; // Increased to 10 minutes for seed data processing

// Simple activity tracking for UX (no longer drives logout logic)
let lastActivityTime = Date.now();

// Session monitoring state (kept for backward compatibility)
let sessionExtensionInterval = null;
let warningShown = false;

// Track user activity
const trackActivity = () => {
  lastActivityTime = Date.now();
  if (process.env.NODE_ENV === "development") {
    console.debug(
      "🔄 Activity tracked:",
      new Date(lastActivityTime).toLocaleTimeString()
    );
  }
};

// No-op activity listeners (backward compatible with old API)
const addActivityListeners = () => {};
const removeActivityListeners = () => {};

// Helper function to handle 401 errors and trigger logout
// This is the single, central place that clears session and dispatches forceLogout
export const handle401Error = (error) => {
  const is401 =
    error?.is401Error === true ||
    error?.status === 401 ||
    error?.response?.status === 401;

  if (!is401) {
    return false;
  }

  // Clear tokens
  localStorage.removeItem("access_token");
  localStorage.removeItem("user");

  // Dispatch logout event (multiple times to ensure it's caught)
  const logoutEvent = new CustomEvent("forceLogout", {
    detail: { reason: "Session expired" },
    bubbles: true,
    cancelable: false,
  });
  window.dispatchEvent(logoutEvent);
  setTimeout(() => {
    window.dispatchEvent(
      new CustomEvent("forceLogout", {
        detail: { reason: "Session expired" },
        bubbles: true,
        cancelable: false,
      })
    );
  }, 0);

  return true;
};

// Check if user is active (within last 5 minutes)
// Reduced from 10 minutes to be more responsive to user activity
const isUserActive = () => {
  const now = Date.now();
  const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
  return now - lastActivityTime < fiveMinutes;
};

// Show session warning
const showSessionWarning = () => {
  if (warningShown) return;
  warningShown = true;

  // Dispatch custom event to show warning
  window.dispatchEvent(
    new CustomEvent("sessionWarning", {
      detail: {
        message:
          "Your session will expire in 2 minutes due to inactivity. Click anywhere to extend.",
      },
    })
  );
};

// Start session monitoring
const startSessionMonitoring = () => {
  if (sessionExtensionInterval) return;

  addActivityListeners();

  // Check session status every 60 seconds (reduced frequency to avoid unnecessary checks)
  sessionExtensionInterval = setInterval(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      stopSessionMonitoring();
      return;
    }

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      // Check if exp claim exists
      if (!payload.exp) {
        // Token doesn't have expiry claim, treat as invalid
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
        window.dispatchEvent(new CustomEvent("forceLogout"));
        stopSessionMonitoring();
        return;
      }

      const timeUntilExpiry = payload.exp - currentTime;

      // Only log out if token is ALREADY expired
      // Don't log out preemptively - let API calls handle 401 responses
      if (timeUntilExpiry <= 0) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
        window.dispatchEvent(new CustomEvent("forceLogout"));
        stopSessionMonitoring();
        return;
      }

      // Show warning if token expires in less than 5 minutes and user is not active
      if (timeUntilExpiry < 300 && !isUserActive()) {
        showSessionWarning();
      }
    } catch (error) {
      // Invalid token format, logout
      console.error("Error parsing token in session monitoring:", error);
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      window.dispatchEvent(new CustomEvent("forceLogout"));
      stopSessionMonitoring();
    }
  }, 60000); // Check every 60 seconds (was 10 seconds - too aggressive)
};

// Stop session monitoring
const stopSessionMonitoring = () => {
  if (sessionExtensionInterval) {
    clearInterval(sessionExtensionInterval);
    sessionExtensionInterval = null;
  }
  removeActivityListeners();
};

// Add request interceptor to include JWT token and check expiration
axios.interceptors.request.use(
  (config) => {
    // Reset session timeout on every API request
    trackActivity();

    const token = localStorage.getItem("access_token");
    if (token) {
      // Check if token is expired before making the request
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const currentTime = Math.floor(Date.now() / 1000);

        // Only reject if token is ALREADY expired (not about to expire)
        // Let the server handle near-expiry tokens - it will return 401 if needed
        if (payload.exp && payload.exp < currentTime) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("user");
          window.dispatchEvent(new CustomEvent("forceLogout"));
          stopSessionMonitoring();
          return Promise.reject(new Error("Token expired"));
        }
      } catch (error) {
        // Invalid token format, trigger logout
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
        window.dispatchEvent(new CustomEvent("forceLogout"));
        return Promise.reject(new Error("Invalid token"));
      }

      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors (token expiration) and reset session timeout
axios.interceptors.response.use(
  (response) => {
    // Reset session timeout on successful API response
    trackActivity();
    return response;
  },
  (error) => {
    // Reset session timeout even on error (user is still active if making API calls)
    // Only reset if it's not a 401 (unauthorized) error
    if (!error.response || error.response.status !== 401) {
      trackActivity();
    }

    if (error.response && error.response.status === 401) {
      // 401 means token is invalid/expired, logout immediately
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      stopSessionMonitoring();
      // Dispatch custom event to trigger logout in App.js (synchronously)
      const logoutEvent = new CustomEvent("forceLogout", {
        detail: { reason: "Session expired" },
        bubbles: true,
        cancelable: false,
      });
      window.dispatchEvent(logoutEvent);
      // Also try immediate dispatch to ensure it's processed
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("forceLogout", {
            detail: { reason: "Session expired" },
            bubbles: true,
            cancelable: false,
          })
        );
      }, 0);
      // Create error with specific marker for 401 detection
      const logoutError = new Error("Session expired - please log in again");
      logoutError.is401Error = true;
      logoutError.status = 401;
      return Promise.reject(logoutError);
    }

    // For other errors, show the original error
    return Promise.reject(error);
  }
);

// Helper function for fetch requests with proper base URL and authentication
export const apiFetch = async (url, options = {}) => {
  // Reset session timeout on every API call
  trackActivity();

  const fullUrl = url.startsWith("http") ? url : `${API_BASE_URL}${url}`;

  // Get token from localStorage
  const token = localStorage.getItem("access_token");

  // Check if token is expired before making the request
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      // Only reject if token is ALREADY expired (not about to expire)
      // Let the server handle near-expiry tokens - it will return 401 if needed
      if (payload.exp && payload.exp < currentTime) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
        window.dispatchEvent(new CustomEvent("forceLogout"));
        stopSessionMonitoring();
        throw new Error("Token expired");
      }
    } catch (error) {
      // Only treat as invalid token if it's a parsing error, not if it's our own "Token expired" error
      if (error.message !== "Token expired") {
        console.error("Error parsing token in apiFetch:", error);
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
        window.dispatchEvent(new CustomEvent("forceLogout"));
        stopSessionMonitoring();
        throw new Error("Invalid token");
      }
      throw error;
    }
  }

  // Add authentication header if token exists
  const headers = {
    ...options.headers,
  };

  // Only set Content-Type to application/json if body is not FormData
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Add timeout handling
  const timeout = options.timeout || 600000; // Default 10 minutes timeout (increased for seed data processing)

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(fullUrl, {
      ...options,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Reset session timeout on successful API response
    trackActivity();

    // Handle 401 errors (token expiration) except for explicit auth endpoints
    const originalPath = typeof url === "string" ? url : "";
    const isAuthLogin = originalPath.includes("/api/auth/login");
    const isAuthSignup = originalPath.includes("/api/auth/signup");
    const isAuthSsoExchange = originalPath.includes("/api/auth/sso/exchange");
    const isAuthOidcExchange = originalPath.includes("/api/auth/oidc/exchange");
    const isChangePassword = originalPath.includes("/api/auth/change-password");
    if (
      response.status === 401 &&
      !isAuthLogin &&
      !isAuthSignup &&
      !isAuthSsoExchange &&
      !isAuthOidcExchange &&
      !isChangePassword
    ) {
      // 401 means token is invalid/expired, logout immediately
      // Clear tokens first
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      // Stop session monitoring
      stopSessionMonitoring();
      // Dispatch custom event to trigger logout in App.js (synchronously)
      const logoutEvent = new CustomEvent("forceLogout", {
        detail: { reason: "Session expired" },
        bubbles: true,
        cancelable: false,
      });
      window.dispatchEvent(logoutEvent);
      // Also try immediate dispatch to ensure it's processed
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("forceLogout", {
            detail: { reason: "Session expired" },
            bubbles: true,
            cancelable: false,
          })
        );
      }, 0);
      // Create error with specific marker for 401 detection
      const sessionError = new Error("Session expired - please log in again");
      sessionError.is401Error = true;
      sessionError.status = 401;
      throw sessionError;
    }

    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name !== "AbortError") {
      trackActivity();
    }

    // Note: We intentionally do NOT logout on network errors ("Failed to fetch")
    // Network errors are temporary and should not force a logout
    // Only 401 responses from the server should trigger logout

    if (error.name === "AbortError") {
      throw new Error(`Request timeout after ${timeout}ms`);
    }
    throw error;
  }
};

/** Submit structured Q&A assistant feedback (rating + incident bundle). */
export async function submitQaFeedback(payload) {
  const response = await apiFetch("/api/qa/feedback", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg =
      (typeof data.detail === "string" && data.detail) ||
      data.error ||
      "Failed to submit feedback";
    throw new Error(msg);
  }
  return data;
}

export async function fetchReviewWorkflowContext() {
  const response = await apiFetch(`/api/review/workflow-context?_t=${Date.now()}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to load review workflow context"
    );
  }
  return data;
}

export async function submitBaselineApprovalSubmission({
  entityIds,
  approverGroupId,
  comment = "",
}) {
  const ids = Array.isArray(entityIds)
    ? entityIds.map((id) => String(id || "").trim()).filter(Boolean)
    : [];
  const response = await apiFetch("/api/review/baseline-submissions", {
    method: "POST",
    body: JSON.stringify({
      entity_ids: ids,
      approver_group_id: String(approverGroupId || "").trim(),
      comment: String(comment || "").trim(),
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to submit for baseline approval"
    );
  }
  return data;
}

export async function materializeReviewItems(runId, options = {}) {
  const response = await apiFetch(
    `/api/review/runs/${encodeURIComponent(runId)}/materialize-items`,
    {
      method: "POST",
      body: JSON.stringify({
        recompute_existing: Boolean(options.recompute_existing),
        approver_group_id: options.approver_group_id || null,
      }),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to materialize review items"
    );
  }
  return data;
}

export async function fetchReviewItems(params = {}) {
  const query = new URLSearchParams();
  if (params.state) query.set("state", params.state);
  if (params.assignee) query.set("assignee", params.assignee);
  if (params.assignee_group_id)
    query.set("assignee_group_id", params.assignee_group_id);
  if (params.run_id) query.set("run_id", params.run_id);
  if (params.kind) query.set("kind", params.kind);
  if (params.entity_type) query.set("entity_type", params.entity_type);
  if (params.submitted_by) query.set("submitted_by", params.submitted_by);
  if (params.gallery_only) query.set("gallery_only", "true");
  query.set("page", String(params.page || 1));
  query.set("page_size", String(params.page_size || 25));
  query.set("_t", String(Date.now()));

  const response = await apiFetch(`/api/review/items?${query.toString()}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to load review items"
    );
  }
  return data;
}

/** Pending approver inbox + unseen rejection updates for the current submitter. */
export async function fetchBaselineApprovalNotificationCount() {
  const response = await apiFetch(
    `/api/review/notification-summary?_t=${Date.now()}`
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to load review notification summary"
    );
  }
  const pendingApprover =
    (Number(data.pending_l1_total) || 0) + (Number(data.pending_l2_total) || 0);
  const unseenRejections = countUnseenBaselineRejections(
    data.my_rejected_gallery || []
  );
  return pendingApprover + unseenRejections;
}

/** @deprecated use fetchBaselineApprovalNotificationCount */
export async function fetchPendingBaselineApprovalCount() {
  return fetchBaselineApprovalNotificationCount();
}

export async function bulkReviewItemsAction({ itemIds, action, note, reasonCode }) {
  const response = await apiFetch("/api/review/items/bulk-action", {
    method: "POST",
    body: JSON.stringify({
      item_ids: itemIds,
      action,
      note: note || "",
      reason_code: reasonCode || "",
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Bulk action failed"
    );
  }
  return data;
}

export async function approveReviewItemL1(itemId, note = "") {
  const response = await apiFetch(
    `/api/review/items/${encodeURIComponent(itemId)}/approve-l1`,
    {
      method: "POST",
      body: JSON.stringify({ note }),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to approve item"
    );
  }
  return data;
}

export async function approveReviewItemL2(itemId, note = "") {
  const response = await apiFetch(
    `/api/review/items/${encodeURIComponent(itemId)}/approve-l2`,
    {
      method: "POST",
      body: JSON.stringify({ note }),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to approve at L2"
    );
  }
  return data;
}

export async function fetchReviewQueueAccess() {
  const response = await apiFetch("/api/review/access");
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return { can_access: false };
  }
  return { can_access: Boolean(data?.in_approver_group), in_approver_group: Boolean(data?.in_approver_group), raw: data };
}

export async function rejectReviewItem(itemId, reasonCode, note = "") {
  const response = await apiFetch(
    `/api/review/items/${encodeURIComponent(itemId)}/reject`,
    {
      method: "POST",
      body: JSON.stringify({ reason_code: reasonCode, note }),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to reject item"
    );
  }
  return data;
}

export async function getReviewPolicy() {
  const endpoints = ["/api/review/policy", "/api/admin/review/policy"];
  let lastError = null;
  for (const endpoint of endpoints) {
    const response = await apiFetch(endpoint);
    const data = await response.json().catch(() => ({}));
    if (response.ok) {
      return data?.data || {};
    }
    // If endpoint is missing, try fallback endpoint before failing.
    if (response.status === 404) {
      lastError = new Error(
        (typeof data.detail === "string" && data.detail) ||
          data.error ||
          "No policies configured yet"
      );
      continue;
    }
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to load review policy"
    );
  }
  // Both endpoints not found: treat as unconfigured policy state, not a hard failure.
  return {
    enable_l2_approval: false,
    extractor_cannot_approve_own: false,
    require_reason_on_reject: true,
    require_reason_on_request_changes: true,
    _not_configured: true,
    _message: lastError?.message || "No policies configured yet",
  };
}

export async function updateReviewPolicy(policy) {
  const payload = JSON.stringify({
    enable_l2_approval: Boolean(policy?.enable_l2_approval),
    extractor_cannot_approve_own: Boolean(
      policy?.extractor_cannot_approve_own
    ),
    require_reason_on_reject: Boolean(policy?.require_reason_on_reject),
    require_reason_on_request_changes: Boolean(
      policy?.require_reason_on_request_changes
    ),
  });
  const endpoints = ["/api/review/policy", "/api/admin/review/policy"];
  let lastError = null;
  for (const endpoint of endpoints) {
    const response = await apiFetch(endpoint, {
      method: "PUT",
      body: payload,
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok) {
      return data;
    }
    if (response.status === 404) {
      lastError = new Error(
        (typeof data.detail === "string" && data.detail) ||
          data.error ||
          "Review policy API endpoint not available"
      );
      continue;
    }
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to update review policy"
    );
  }
  throw lastError || new Error("Failed to update review policy");
}

export async function fetchAuthUsers() {
  const response = await apiFetch("/api/auth/users");
  const data = await response.json().catch(() => []);
  if (!response.ok) {
    throw new Error("Failed to load users");
  }
  return Array.isArray(data) ? data : [];
}

export async function fetchAgentWriteProposals({ status = "pending", limit = 50, offset = 0 } = {}) {
  const qs = new URLSearchParams({
    status: String(status || "pending"),
    limit: String(limit),
    offset: String(offset),
  });
  const response = await apiFetch(`/api/agent/proposals?${qs.toString()}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to load agent proposals"
    );
  }
  return data;
}

export async function resolveAgentWriteProposal(proposalId, decision) {
  const response = await apiFetch(
    `/api/agent/proposals/${encodeURIComponent(proposalId)}/resolve`,
    {
      method: "POST",
      body: JSON.stringify({ decision }),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to resolve proposal"
    );
  }
  return data;
}

export async function fetchLlmUsageSummary({ since, until, admin = false } = {}) {
  const qs = new URLSearchParams();
  if (since) qs.set("since", since);
  if (until) qs.set("until", until);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  // Tenant members use /api/llm-usage; org admins may use the admin route.
  const path = admin ? "/api/admin/llm-usage" : "/api/llm-usage";
  const response = await apiFetch(`${path}${suffix}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to load LLM usage"
    );
  }
  return data;
}

export async function fetchSecurityEvents({
  event_type,
  since,
  until,
  limit = 200,
  offset = 0,
} = {}) {
  const qs = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (event_type) qs.set("event_type", event_type);
  if (since) qs.set("since", since);
  if (until) qs.set("until", until);
  const response = await apiFetch(`/api/admin/security-events?${qs.toString()}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to load security events"
    );
  }
  return data;
}

export async function getApproverGroups() {
  const response = await apiFetch(
    `/api/review/approver-groups?_t=${encodeURIComponent(String(Date.now()))}`
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to load approver groups"
    );
  }
  return Array.isArray(data?.data) ? data.data : [];
}

/** Batch-check source/target entity-type pairs against tenant metamodel / DB rules (same as entity type change validation). */
export async function validateEnrichmentRelationshipPairs(pairs) {
  const response = await apiFetch(
    "/api/application-enrichment/validate-relationship-pairs",
    {
      method: "POST",
      body: JSON.stringify({
        pairs: Array.isArray(pairs)
          ? pairs.map((p) => ({
              source_type: String(p?.source_type || "").trim(),
              target_type: String(p?.target_type || "").trim(),
            }))
          : [],
      }),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Relationship pair validation failed"
    );
  }
  const inner = data?.data ?? data;
  return inner?.results ?? [];
}

/** Validate pairs and resolve relationship_type from metamodel (enrichment type-change repair). */
export async function reconcileEnrichmentRelationshipPairs(pairs) {
  const response = await apiFetch(
    "/api/application-enrichment/reconcile-relationship-pairs",
    {
      method: "POST",
      body: JSON.stringify({
        pairs: Array.isArray(pairs)
          ? pairs.map((p) => ({
              source_type: String(p?.source_type || "").trim(),
              target_type: String(p?.target_type || "").trim(),
              relationship_type: String(p?.relationship_type || "").trim() || undefined,
            }))
          : [],
      }),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Relationship reconciliation failed"
    );
  }
  const inner = data?.data ?? data;
  return inner?.results ?? [];
}

export async function createApproverGroup(payload) {
  const response = await apiFetch("/api/review/approver-groups", {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to create approver group"
    );
  }
  return data;
}

export async function updateApproverGroup(groupId, payload) {
  const response = await apiFetch(
    `/api/review/approver-groups/${encodeURIComponent(groupId)}`,
    {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to update approver group"
    );
  }
  return data;
}

export async function deleteApproverGroup(groupId) {
  const response = await apiFetch(
    `/api/review/approver-groups/${encodeURIComponent(groupId)}`,
    {
      method: "DELETE",
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to delete approver group"
    );
  }
  return data;
}

export async function setApproverGroupMembers(groupId, members) {
  const normalizedMembers = Array.isArray(members)
    ? members
        .map((m) => String(m ?? "").trim())
        .filter(Boolean)
    : [];
  const response = await apiFetch(
    `/api/review/approver-groups/${encodeURIComponent(groupId)}/members`,
    {
      method: "PUT",
      body: JSON.stringify({
        members: normalizedMembers,
      }),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (typeof data.detail === "string" && data.detail) ||
        data.error ||
        "Failed to update group members"
    );
  }
  return data;
}

// Retry configuration for AI processing
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 2000, // 2 seconds
  maxDelay: 30000, // 30 seconds max delay
};

// Helper function to calculate delay with exponential backoff
const calculateDelay = (attempt) => {
  const delay = RETRY_CONFIG.baseDelay * Math.pow(2, attempt);
  return Math.min(delay, RETRY_CONFIG.maxDelay);
};

// Helper function to wait
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Connection health check
const checkConnectionHealth = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/health`, {
      timeout: 5000,
    });
    return response.status === 200;
  } catch (error) {
    console.warn("⚠️ Connection health check failed:", error.message);
    return false;
  }
};

// API service functions
export const api = {
  // Get entity names for auto-complete
  async getEntityNames() {
    try {
      const response = await axios.get("/api/entity-names");
      return response.data;
    } catch (error) {
      console.error("❌ Failed to get entity names:", error);
      throw new Error(
        error.response?.data?.detail ||
          error.message ||
          "Failed to get entity names"
      );
    }
  },
  // Refine system description
  async refineDescription(description) {
    console.log("🔧 API Call: Refining description:", description);
    console.log("🌐 API URL:", `${API_BASE_URL}/api/refine-description`);

    try {
      const response = await axios.post("/api/refine-description", {
        text: description,
        timestamp: new Date().toISOString(),
      });

      return response.data;
    } catch (error) {
      console.error("❌ API Error:", error);
      console.error("❌ Error Details:", error.response?.data);
      throw new Error(
        error.response?.data?.detail || error.message || "Network Error"
      );
    }
  },

    /** Queue knowledge ingestion (extract_mode auto | force_on | force_off). */
  async queueKnowledgeJob(anchorKind, anchorId, sources, extractModeOrOptions = "auto") {
    const body = {
      anchor_kind: String(anchorKind || "entity").trim(),
      anchor_id: String(anchorId || "").trim(),
      sources: Array.isArray(sources) ? sources : [],
    };

    const applyMode = (mode) => {
      if (mode === "auto" || mode === "force_on" || mode === "force_off") {
        body.extract_mode = mode;
      } else {
        body.extract_mode = "auto";
      }
      if (body.extract_mode === "force_on") {
        body.extract_entities = true;
      }
    };

    if (typeof extractModeOrOptions === "boolean") {
      if (extractModeOrOptions) {
        body.extract_entities = true;
        body.extract_mode = "force_on";
      } else {
        body.extract_mode = "auto";
      }
    } else if (
      extractModeOrOptions &&
      typeof extractModeOrOptions === "object" &&
      !Array.isArray(extractModeOrOptions)
    ) {
      const { extractMode, extractEntities } = extractModeOrOptions;
      let mode = extractMode;
      if (!mode && extractEntities === true) {
        mode = "force_on";
      }
      if (!mode) {
        mode = "auto";
      }
      applyMode(mode);
      if (extractEntities === true && body.extract_mode !== "force_on") {
        body.extract_entities = true;
      }
    } else if (typeof extractModeOrOptions === "string") {
      applyMode(extractModeOrOptions);
    } else {
      body.extract_mode = "auto";
    }

    const response = await axios.post("/api/knowledge/jobs", body);
    return response?.data?.data || response?.data || {};
  },

  async listKnowledgeSurfaces() {
    const response = await axios.get("/api/knowledge/surfaces");
    const data = response?.data?.data || response?.data || {};
    return data.surfaces || [];
  },

  async getKnowledgeLibrarySummary() {
    const response = await axios.get("/api/knowledge/library/summary");
    return response?.data?.data || response?.data || {};
  },

  async getKnowledgeSurfaceDetail(surfaceId) {
    const response = await axios.get(
      `/api/knowledge/surfaces/${encodeURIComponent(String(surfaceId || "").trim())}`
    );
    return response?.data?.data || response?.data || {};
  },

  async listEntityKnowledgeSources(entityId) {
    const response = await axios.get(
      `/api/knowledge/entities/${encodeURIComponent(String(entityId || "").trim())}/sources`
    );
    return response?.data?.data || response?.data || {};
  },

  async getEvidenceIndexHealth({ entityId = null, limit = 100 } = {}) {
    const params = { limit };
    if (entityId) params.entity_id = String(entityId).trim();
    const response = await axios.get("/api/evidence/index/health", { params });
    return response?.data || {};
  },

  async reindexEvidenceIndex({
    dryRun = false,
    limit = 0,
    entityId = null,
    sourceVersionId = null,
    embedMissing = true,
  } = {}) {
    const response = await axios.post("/api/evidence/index/reindex", {
      dry_run: Boolean(dryRun),
      limit: Number(limit) || 0,
      entity_id: entityId ? String(entityId).trim() : null,
      source_version_id: sourceVersionId
        ? String(sourceVersionId).trim()
        : null,
      embed_missing: Boolean(embedMissing),
    });
    return response?.data || {};
  },

  async getKnowledgeSourceDetail(sourceVersionId) {
    const response = await axios.get(
      `/api/knowledge/sources/${encodeURIComponent(String(sourceVersionId || "").trim())}`
    );
    return response?.data?.data || response?.data || {};
  },

  async searchKnowledgeSources(query, { surfaceId = null, entityId = null } = {}) {
    const params = { q: String(query || "").trim() };
    if (surfaceId) params.surface_id = String(surfaceId).trim();
    if (entityId) params.entity_id = String(entityId).trim();
    const response = await axios.get("/api/knowledge/sources/search", { params });
    const data = response?.data?.data || response?.data || {};
    return data.sources || [];
  },

  async unscopeKnowledgeSource(sourceVersionId, { anchor_kind, anchor_id }) {
    const response = await axios.post(
      `/api/knowledge/sources/${encodeURIComponent(String(sourceVersionId || "").trim())}/unscope`,
      {
        anchor_kind: String(anchor_kind || "").trim(),
        anchor_id: String(anchor_id || "").trim(),
      }
    );
    return response?.data?.data || response?.data || {};
  },

  async purgeKnowledgeSource(sourceVersionId) {
    const response = await axios.post(
      `/api/knowledge/sources/${encodeURIComponent(String(sourceVersionId || "").trim())}/purge`,
      { confirm: true }
    );
    return response?.data?.data || response?.data || {};
  },

  async createKnowledgeSurface(payload) {
    const response = await axios.post("/api/knowledge/surfaces", payload);
    return response?.data?.data || response?.data || {};
  },

  /** Queue application enrichment in background; poll GET /api/jobs/{jobId}. */
  async queueApplicationEnrichmentJob(anchorApplicationId, sources) {
    const response = await axios.post("/api/application-enrichment/jobs", {
      anchor_application_id: String(anchorApplicationId || "").trim(),
      sources: Array.isArray(sources) ? sources : [],
    });
    return response?.data?.data || response?.data || {};
  },

  /**
   * Queue application enrichment from pasted text. Backend picks anchor application by matching
   * catalog application names/aliases against the description.
   */
  /** Regenerate RAG summaries for specific entities (e.g. after deferred enrichment review). */
  async regenerateRagSummariesBatch(entityIds, options = {}) {
    const ids = (Array.isArray(entityIds) ? entityIds : [])
      .map((id) => String(id || "").trim())
      .filter(Boolean);
    if (!ids.length) return { success: true, results: { total: 0 } };
    const background = options.background !== false;
    const response = await axios.post(
      "/api/rag/summaries/generate-batch",
      { entity_ids: ids },
      {
        params: background ? { background: true } : { background: false },
        timeout: background ? 8000 : 300000,
      }
    );
    return response?.data || {};
  },

  async queueApplicationEnrichmentFromDescription(
    description,
    sourceLabel = null,
    anchorApplicationId = null
  ) {
    const ENRICH_QUEUE_TIMEOUT_MS = 20000;
    const body = {
      description: String(description || "").trim(),
      ...(sourceLabel ? { source_label: String(sourceLabel).trim() } : {}),
    };
    const anchorId = String(anchorApplicationId || "").trim();
    if (anchorId) body.anchor_application_id = anchorId;
    const response = await axios.post(
      "/api/application-enrichment/jobs/from-description",
      body,
      { timeout: ENRICH_QUEUE_TIMEOUT_MS }
    );
    return response?.data?.data || response?.data || {};
  },

  /** Chunks for a completed application enrichment job (`result.evidence.ingestion_run_id`). */
  async getApplicationEnrichmentEvidenceChunks(ingestionRunId) {
    const id = String(ingestionRunId || "").trim();
    if (!id) throw new Error("ingestion_run_id is required");
    const response = await axios.get(
      `/api/application-enrichment/evidence/ingestion-runs/${encodeURIComponent(id)}/chunks`
    );
    return response?.data || {};
  },

  // Get entities with pagination and filtering
  async getEntities(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      // Add pagination parameters
      if (params.page) queryParams.append("page", params.page);
      const pageSize = params.pageSize ?? params.page_size;
      if (pageSize) queryParams.append("page_size", pageSize);

      // Add filter parameters
      if (params.search) queryParams.append("search", params.search);
      if (params.type) queryParams.append("type", params.type);
      if (params.businessCapability)
        queryParams.append("business_capability", params.businessCapability);
      if (params.technology)
        queryParams.append("technology", params.technology);
      if (params.lifecycleStatus)
        queryParams.append("lifecycle_status", params.lifecycleStatus);
      if (params.criticality)
        queryParams.append("criticality", params.criticality);
      if (params.minConfidence)
        queryParams.append("min_confidence", params.minConfidence);
      if (params.sortBy) queryParams.append("sort_by", params.sortBy);
      if (params.sortDesc !== undefined)
        queryParams.append("sort_desc", params.sortDesc);
      if (params._t) queryParams.append("_t", params._t); // Cache-busting timestamp
      if (params._r) queryParams.append("_r", params._r); // Cache-busting random

      const url = `/api/entities?${queryParams.toString()}`;
      const response = await axios.get(url);

      // Validate response structure and provide defaults
      const data = response.data || {};
      return {
        entities: data.entities || [],
        total: data.total || 0,
        page: data.page || 1,
        page_size: data.page_size || 25,
        total_pages: data.total_pages || 0,
      };
    } catch (error) {
      console.error("Failed to fetch entities:", error);
      // Return safe defaults on error
      return {
        entities: [],
        total: 0,
        page: 1,
        page_size: 25,
        total_pages: 0,
      };
    }
  },

  // In-memory hierarchy node cache (session). Keyed by type + parent.
  _hierarchyNodesCache: new Map(),

  _hierarchyCacheKey(entityType, parentId = null) {
    const type = String(entityType || "")
      .trim()
      .toLowerCase()
      .replace(/_/g, "-")
      .replace(/\s+/g, "-");
    const parent =
      parentId == null || parentId === "" ? "__roots__" : String(parentId);
    return `${type}::${parent}`;
  },

  invalidateHierarchyNodesCache(entityType = null) {
    if (!entityType) {
      this._hierarchyNodesCache.clear();
      return;
    }
    const prefix = `${String(entityType || "")
      .trim()
      .toLowerCase()
      .replace(/_/g, "-")
      .replace(/\s+/g, "-")}::`;
    for (const key of Array.from(this._hierarchyNodesCache.keys())) {
      if (key.startsWith(prefix)) this._hierarchyNodesCache.delete(key);
    }
  },

  // Get hierarchy nodes for lazy-loaded trees (roots or children of a parent)
  async getHierarchyNodes(entityType, parentId = null, forceRefresh = false) {
    const cacheKey = this._hierarchyCacheKey(entityType, parentId);
    if (!forceRefresh && this._hierarchyNodesCache.has(cacheKey)) {
      return this._hierarchyNodesCache.get(cacheKey);
    }
    try {
      const params = new URLSearchParams();
      params.append("type", entityType || "");
      if (parentId != null && parentId !== "") params.append("parent_id", parentId);
      if (forceRefresh) params.append("_", String(Date.now()));
      const response = await axios.get(
        `/api/entities/hierarchy-nodes?${params.toString()}`,
        forceRefresh ? { cache: "no-store" } : undefined
      );
      const nodes = Array.isArray(response.data) ? response.data : [];
      this._hierarchyNodesCache.set(cacheKey, nodes);
      return nodes;
    } catch (error) {
      console.error("Failed to fetch hierarchy nodes:", error);
      return [];
    }
  },

  /**
   * Fast catalog suggestions for Add-relationship dialogs.
   * GET /api/entities/{id}/relationship-suggestions?target_type=...
   */
  async getRelationshipSuggestions(
    entityId,
    targetType,
    { limit = 3, excludeIds = [] } = {}
  ) {
    const params = new URLSearchParams();
    params.append("target_type", targetType || "");
    params.append("limit", String(limit));
    if (excludeIds?.length) {
      params.append("exclude_ids", excludeIds.filter(Boolean).join(","));
    }
    const response = await axios.get(
      `/api/entities/${encodeURIComponent(entityId)}/relationship-suggestions?${params.toString()}`
    );
    return response.data;
  },

  // Get all entities without pagination (for backward compatibility)
  async getEntitiesAll() {
    try {
      const response = await axios.get("/api/entities-all");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch all entities:", error);
      throw new Error(
        error.response?.data?.detail ||
          error.message ||
          "Failed to fetch all entities"
      );
    }
  },

  // Get AI-generated summary for an entity
  async getEntitySummary(entityId) {
    try {
      const response = await axios.get(`/api/entities/${entityId}/summary`);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch entity summary:", error);
      throw new Error(
        error.response?.data?.detail ||
          error.message ||
          "Failed to fetch entity summary"
      );
    }
  },

  async getEntityEnrichmentReviewAudit(entityId) {
    const response = await apiFetch(
      `/api/entities/${encodeURIComponent(entityId)}/enrichment-review-audit`
    );
    if (!response?.ok) {
      const err = await response?.json().catch(() => ({}));
      throw new Error(
        err?.detail || err?.message || "Failed to load enrichment audit trail"
      );
    }
    return response.json();
  },

  // Get specific entity
  async getEntity(
    entityId,
    {
      includeRelationships = false,
      includeRelatedEntities = false,
      bypassCache = false,
      includeUnapproved = true,
      architectureExplorer = false,
      reviewItemId = null,
    } = {}
  ) {
    try {
      const params = {};
      if (includeRelationships) {
        params.include_relationships = true;
      }
      if (includeRelatedEntities) {
        params.include_related_entities = true;
      }
      if (bypassCache) {
        params.bypassCache = true;
        params._t = Date.now();
      }
      if (includeUnapproved) {
        params.includeUnapproved = true;
      }
      if (architectureExplorer) {
        params.architecture_explorer = true;
      }
      if (reviewItemId) {
        params.review_item_id = reviewItemId;
      }
      const response = await axios.get(`/api/entities/${entityId}`, {
        params: Object.keys(params).length ? params : undefined,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch entity:", error);
      throw new Error(
        error.response?.data?.detail ||
          error.message ||
          "Failed to fetch entity"
      );
    }
  },

  /** Propose a catalog description from entity-scoped evidence (preview only). */
  async proposeEntityDescription(entityId, { currentDescription } = {}) {
    try {
      const body = {};
      if (currentDescription !== undefined) {
        body.current_description = currentDescription;
      }
      const response = await axios.post(
        `/api/entities/${encodeURIComponent(entityId)}/propose-description`,
        body
      );
      return response.data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message =
        (typeof detail === "string" && detail) ||
        detail?.message ||
        error.message ||
        "Failed to propose description";
      throw new Error(message);
    }
  },

  /** Propose alias names from entity-scoped evidence (preview only). */
  async discoverEntityAliases(entityId, { currentAliasNames } = {}) {
    try {
      const body = {};
      if (currentAliasNames !== undefined) {
        body.current_alias_names = currentAliasNames;
      }
      const response = await axios.post(
        `/api/entities/${encodeURIComponent(entityId)}/discover-aliases`,
        body
      );
      return response.data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message =
        (typeof detail === "string" && detail) ||
        detail?.message ||
        error.message ||
        "Failed to discover aliases";
      throw new Error(message);
    }
  },

  /** Queue section-targeted refine-from-knowledge (entity_refine job). */
  async refineEntityFromKnowledge(entityId, { sectionKeys } = {}) {
    try {
      const body = {};
      if (sectionKeys !== undefined) {
        body.section_keys = sectionKeys;
      }
      const response = await axios.post(
        `/api/entities/${encodeURIComponent(entityId)}/refine-from-knowledge`,
        body
      );
      return response.data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message =
        (typeof detail === "string" && detail) ||
        detail?.message ||
        error.message ||
        "Failed to start refine from knowledge";
      throw new Error(message);
    }
  },

  // Update entity
  async updateEntity(entityId, updates, options = {}) {
    try {
      const payload =
        options.deferRagRegeneration === true
          ? { ...updates, defer_rag_regeneration: true }
          : updates;
      const response = await axios.put(`/api/entities/${entityId}`, payload);
      return response.data;
    } catch (error) {
      console.error("Failed to update entity:", error);
      const errorResponse = error.response?.data;
      const responseDetail = errorResponse?.detail;
      const status = error.response?.status;

      // Normalize multiple backend error shapes into one consistent error object.
      // Expected FastAPI shape from raise_api_error:
      // { detail: { code, message, detail: {...} } }
      // But some handlers may return: { message, detail } or plain string detail.
      let normalizedDetail = null;
      let normalizedMessage = "";

      if (typeof responseDetail === "object" && responseDetail !== null) {
        normalizedDetail = responseDetail;
        normalizedMessage =
          responseDetail.message ||
          responseDetail.detail?.message ||
          errorResponse?.message ||
          "";
      } else if (typeof responseDetail === "string" && responseDetail.trim()) {
        normalizedMessage = responseDetail;
      } else if (typeof errorResponse?.message === "string") {
        normalizedMessage = errorResponse.message;
      }

      if (!normalizedMessage && typeof errorResponse === "object" && errorResponse) {
        // Last-resort extraction for non-standard 4xx payloads
        normalizedMessage =
          errorResponse.error ||
          errorResponse.reason ||
          (typeof errorResponse.detail === "string" ? errorResponse.detail : "") ||
          "";
      }

      if (!normalizedMessage) {
        normalizedMessage =
          error.message ||
          (status ? `Request failed with status code ${status}` : "") ||
          "Failed to update entity";
      }

      const apiError = new Error(normalizedMessage);
      if (normalizedDetail) {
        apiError.detail = normalizedDetail;
        apiError.code = normalizedDetail.code || errorResponse?.code;
      } else if (errorResponse && typeof errorResponse === "object") {
        apiError.detail = errorResponse;
        apiError.code = errorResponse.code;
      }
      // Preserve original response in case callers need to inspect nested payloads.
      apiError.response = error.response;
      apiError.status = status;
      throw apiError;
    }
  },

  /** Dry-run entity type change; same invalid-edge logic as PUT entity (no DB writes). */
  async previewEntityTypeChange(entityId, newType) {
    const response = await axios.post(
      `/api/entities/${entityId}/type-change-preview`,
      { new_type: newType }
    );
    return response.data;
  },

  // Delete entity
  async deleteEntity(entityId) {
    try {
      const response = await axios.delete(`/api/entities/${entityId}`);
      return response.data;
    } catch (error) {
      console.error("Failed to delete entity:", error);
      throw new Error(
        error.response?.data?.detail ||
          error.message ||
          "Failed to delete entity"
      );
    }
  },

  // Get all relationships
  async getRelationships() {
    try {
      const response = await axios.get("/api/relationships");
      // Extract relationships array from the response object
      const data = response.data || {};
      return data.relationships || [];
    } catch (error) {
      console.error("Failed to fetch relationships:", error);
      // Return empty array on error to prevent reduce errors
      return [];
    }
  },

  // Update relationship
  async updateRelationship(relationshipId, updates) {
    try {
      // Backend expects a flat JSON body of fields to update
      const response = await axios.put(
        `/api/relationships/${relationshipId}`,
        updates
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update relationship:", error);
      throw new Error(
        error.response?.data?.detail ||
          error.message ||
          "Failed to update relationship"
      );
    }
  },

  // Batch relationships (create/update/delete in one call)
  async batchUpdateRelationships({
    create = [],
    update = [],
    delete: del = [],
    cascade_delete_incomplete_interfaces = false,
  }) {
    try {
      const body = {
        create,
        update,
        delete: del,
      };
      if (cascade_delete_incomplete_interfaces) {
        body.cascade_delete_incomplete_interfaces = true;
      }
      const response = await axios.post(`/api/relationships/batch`, body);
      return response.data;
    } catch (error) {
      console.error("Failed to batch update relationships:", error);
      const detail = error.response?.data?.detail;
      const message =
        (typeof detail === "string" && detail) ||
        detail?.message ||
        error.message ||
        "Failed to batch update relationships";
      const err = new Error(message);
      err.code = detail?.code;
      err.field = detail?.field;
      err.incompleteInterfaces =
        detail?.detail?.incomplete_interfaces ||
        detail?.incomplete_interfaces ||
        [];
      throw err;
    }
  },

  // Async ops: start and poll
  async deleteEntityAsync(entityId) {
    const response = await axios.post(`/api/entities/${entityId}/delete-async`);
    return response.data; // { operation_id }
  },

  async mergeEntitiesAsync(sourceEntityId, targetEntityId) {
    try {
      // Use the correct backend endpoint
      const response = await axios.post(
        `/api/entities/${sourceEntityId}/merge`,
        {
          target_entity_id: targetEntityId,
        }
      );

      // The backend returns a synchronous response, so we need to simulate an async operation
      // Create a mock operation ID for compatibility with the polling mechanism
      const mockOperationId = `merge_${sourceEntityId}_${targetEntityId}_${Date.now()}`;

      // Return the expected format for async operations
      return {
        operation_id: mockOperationId,
        message: response.data.message,
        source_entity_id: response.data.source_entity_id,
        target_entity_id: response.data.target_entity_id,
      };
    } catch (err) {
      console.error("Error merging entities:", err);
      throw err;
    }
  },

  async getOperationStatus(operationId) {
    // Handle mock operation IDs for merge operations
    if (operationId && operationId.startsWith("merge_")) {
      // For merge operations, return a completed status immediately
      // since the merge is already done synchronously
      return {
        id: operationId,
        status: "completed",
        progress: 100,
        result: { message: "Entities merged successfully" },
        error: null,
      };
    }

    const response = await axios.get(`/api/ops/${operationId}`);
    return response.data; // { id, status, progress, result, error }
  },

  async createEntitiesBulkHierarchy(entityType, idPrefix, items) {
    const response = await axios.post("/api/entities/bulk-hierarchy", {
      entity_type: entityType,
      id_prefix: idPrefix,
      items: items.map((row) => ({
        pcfId: row.pcfId || "",
        hierarchyId: row.hierarchyId,
        name: row.name,
        elementDescription: row.elementDescription || "",
        parentHierarchyId: row.parentHierarchyId || null,
      })),
    });
    return response.data; // { created, total }
  },

  /** Queue hierarchy import for background processing. Returns { job_id, status, message }. Poll GET /api/jobs/{jobId} for status. */
  async createEntitiesBulkHierarchyBackground(entityType, idPrefix, items) {
    const response = await axios.post("/api/entities/bulk-hierarchy/process", {
      entity_type: entityType,
      id_prefix: idPrefix,
      items: items.map((row) => ({
        pcfId: row.pcfId || "",
        hierarchyId: row.hierarchyId,
        name: row.name,
        elementDescription: row.elementDescription || "",
        parentHierarchyId: row.parentHierarchyId || null,
      })),
    });
    return response.data; // { job_id, status, message }
  },

  async getJobStatus(jobId) {
    const response = await axios.get(`/api/jobs/${jobId}`);
    return response.data;
  },

  /** Persist enrichment review preview + optional audit row on a completed application_enrichment job. */
  async patchJobEnrichmentReview(jobId, { preview, audit_event: auditEvent } = {}) {
    const body = {
      preview,
      audit_event: auditEvent || null,
    };
    try {
      const response = await axios.patch(
        `/api/jobs/${jobId}/enrichment-review`,
        body
      );
      return response.data;
    } catch (err) {
      const status = err?.response?.status;
      // Transient DB disconnects surface as 500 — one automatic retry.
      if (status === 500 || status === 502 || status === 503 || !err?.response) {
        const response = await axios.patch(
          `/api/jobs/${jobId}/enrichment-review`,
          body
        );
        return response.data;
      }
      throw err;
    }
  },

  async createEntityAsync(entityData) {
    try {
      const response = await axios.post("/api/async/entities/create", {
        entity: entityData,
      });
      return response.data; // { operation_id }
    } catch (err) {
      // Fallbacks: legacy async route, then synchronous create
      if (err?.response?.status === 404 || err?.response?.status === 405) {
        try {
          const response2 = await axios.post("/api/entities/create-async", {
            entity: entityData,
          });
          return response2.data;
        } catch (err2) {
          // Final fallback: synchronous entity creation
          const syncResp = await axios.post("/api/entities", entityData);
          return { operation_id: null, entity: syncResp.data };
        }
      }
      // Unknown error: try synchronous as last resort
      try {
        const syncResp = await axios.post("/api/entities", entityData);
        return { operation_id: null, entity: syncResp.data };
      } catch (err3) {
        throw err3;
      }
    }
  },

  // Entity Subtype Management
  async getEntitySubtypes(entityType = null, cacheBuster = null) {
    // If cacheBuster is provided, skip cache
    const forceRefresh = !!cacheBuster;

    // Check local cache if no force refresh
    if (!forceRefresh) {
      const allCached = entitySubtypesCache.get();
      if (allCached && allCached.length > 0) {
        // Filter by entity_type if provided
        const filtered = entityType
          ? allCached.filter((s) => s.entity_type === entityType)
          : allCached;

        if (filtered.length > 0) {
          return { subtypes: filtered, success: true };
        }
      }
    }

    let url = entityType
      ? `/api/entity-subtypes?entity_type=${entityType}`
      : "/api/entity-subtypes";

    if (cacheBuster) {
      url += (url.includes("?") ? "&" : "?") + cacheBuster;
    }

    const response = await axios.get(url);
    const data = response.data;

    return data;
  },

  async createEntitySubtype(subtypeData) {
    const response = await axios.post("/api/entity-subtypes", subtypeData);

    // Invalidate local cache (will be reloaded when needed)
    entitySubtypesCache.invalidate();

    return response.data;
  },

  async updateEntitySubtype(subtypeId, updates) {
    const response = await axios.put(
      `/api/entity-subtypes/${subtypeId}`,
      updates
    );

    // Invalidate local cache (will be reloaded when needed)
    entitySubtypesCache.invalidate();

    return response.data;
  },

  async deleteEntitySubtype(subtypeId) {
    const response = await axios.delete(`/api/entity-subtypes/${subtypeId}`);

    // Invalidate local cache (will be reloaded when needed)
    entitySubtypesCache.invalidate();

    return response.data;
  },

  // Create relationship
  async createRelationship(relationshipData, options = {}) {
    try {
      const payload =
        options.deferRagRegeneration === true
          ? { ...relationshipData, defer_rag_regeneration: true }
          : relationshipData;
      const response = await axios.post("/api/relationships", payload);
      return response.data;
    } catch (error) {
      console.error("Failed to create relationship:", error);
      const detail = error.response?.data?.detail;
      let message =
        (typeof detail === "string" && detail) ||
        (detail && typeof detail === "object" && detail.message) ||
        error.response?.data?.message ||
        error.message ||
        "Failed to create relationship";
      if (detail && typeof detail === "object" && !detail.message && detail.end) {
        const endLabel = detail.end === "source" ? "Source" : "Target";
        message = `${endLabel} entity not found`;
      }
      throw new Error(message);
    }
  },

  // Delete relationship
  async deleteRelationship(relationshipId) {
    try {
      const response = await axios.delete(
        `/api/relationships/${relationshipId}`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to delete relationship:", error);
      throw new Error(
        error.response?.data?.detail ||
          error.message ||
          "Failed to delete relationship"
      );
    }
  },

  // Create entity
  async createEntity(entityData, options = {}) {
    try {
      const payload =
        options.deferRagRegeneration === true
          ? { ...entityData, defer_rag_regeneration: true }
          : entityData;
      const response = await axios.post("/api/entities", payload);
      return response.data;
    } catch (error) {
      console.error("Failed to create entity:", error);
      throw new Error(
        error.response?.data?.detail ||
          error.message ||
          "Failed to create entity"
      );
    }
  },

  // Batch map writes (entities + relationships in one request)
  async mapWrites({
    entities = [],
    relationships = [],
    deferRagRegeneration = true,
    evidence = null,
  }) {
    const body = {
      entities,
      relationships,
      defer_rag_regeneration: Boolean(deferRagRegeneration),
    };
    if (evidence && typeof evidence === "object") {
      body.evidence = evidence;
    }
    const response = await axios.post("/api/map/writes", body);
    return response.data;
  },

  // New functions for entity merging
  async mergeEntities(sourceEntityId, targetEntityId) {
    try {
      const response = await axios.post(
        `/api/entities/${sourceEntityId}/merge`,
        {
          target_entity_id: targetEntityId,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to merge entities:", error);
      throw new Error(
        error.response?.data?.detail ||
          error.message ||
          "Failed to merge entities"
      );
    }
  },

  // Get entities directly from database with all fields
  async getEntitiesFromDatabase(limit = 50, offset = 0) {
    try {
      const response = await axios.get(
        `/api/entities-from-database?limit=${limit}&offset=${offset}`,
        {
          timeout: 10000, // Reduced to 10 seconds timeout
        }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to get entities from database:", error);
      // Return empty data instead of throwing error to prevent UI crashes
      return {
        entities: [],
        total: 0,
        limit: limit,
        offset: offset,
      };
    }
  },

  // Relationship Rules API
  async getRelationshipRules() {
    try {
      const response = await axios.get("/api/relationship-rules", {
        timeout: 10000,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to get relationship rules:", error);
      return {
        relationship_rules: [],
        total: 0,
      };
    }
  },

  async getRelationshipTypes() {
    try {
      const response = await axios.get("/api/relationship-types", {
        timeout: 10000,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to get relationship types:", error);
      return {
        relationship_types: [],
        total: 0,
      };
    }
  },
};

// Entity Types - now using static TYPE_PROFILE enum since entity types don't change
// Returns array of { value, label, icon, color }
export async function getEntityTypesList(_forceRefresh = false) {
  const { getCatalogEntityTypes } = await import("../utils/typeProfile");

  const entityTypesList = getCatalogEntityTypes().map((profile) => ({
    value: profile.value,
    label: profile.label,
    icon: profile.icon,
    color: profile.color,
  }));

  return entityTypesList;
}

/** Enrichment review roster: apps, interfaces, and technology types only. */
export async function getEnrichmentReviewEntityTypesList(_forceRefresh = false) {
  const { getEnrichmentReviewEntityTypes } = await import("../utils/typeProfile");

  return getEnrichmentReviewEntityTypes().map((profile) => ({
    value: profile.value,
    label: profile.label,
    icon: profile.icon,
    color: profile.color,
  }));
}

// Document generation functions
export const generateDocument = async (
  entityId,
  templatePathOrOptions = "solution_blueprint_application.yaml",
  format = "markdown"
) => {
  try {
    let kind = "solution_blueprint";
    let templatePath = "solution_blueprint_application.yaml";
    let outputFormat = format;

    if (
      templatePathOrOptions &&
      typeof templatePathOrOptions === "object" &&
      !Array.isArray(templatePathOrOptions)
    ) {
      kind = templatePathOrOptions.kind || kind;
      templatePath = templatePathOrOptions.templatePath || templatePath;
      outputFormat = templatePathOrOptions.format || outputFormat;
    } else if (typeof templatePathOrOptions === "string") {
      templatePath = templatePathOrOptions;
    }

    // Use apiFetch for consistency with other API calls - it handles CORS, auth, and base URL correctly
    const response = await apiFetch("/api/documents/generate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        kind,
        template_path: templatePath,
        entity_id: entityId,
        format: outputFormat,
      }),
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || "Document generation failed");
    }

    const templateSource =
      response.headers.get("X-Template-Source") || null;
    const templateName = response.headers.get("X-Template-Name") || null;

    // Handle binary responses (DOCX, PDF)
    if (outputFormat === "docx" || outputFormat === "pdf") {
      const blob = await response.blob();
      const filename =
        response.headers
          .get("Content-Disposition")
          ?.split("filename=")[1]
          ?.replace(/"/g, "") || `document.${outputFormat}`;

      // Track usage for document generation
      try {
        const trackResponse = await apiFetch("/api/auth/track-usage", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            usage_type: "document_generation",
            increment: 1,
          }),
        });
        if (trackResponse.ok) {
          console.log("✅ Tracked usage: 1 document generation");
        }
      } catch (error) {
        console.error("Error tracking usage:", error);
        // Don't block the flow if tracking fails
      }

      return {
        blob,
        filename,
        format: outputFormat,
        isBinary: true,
        templateSource,
        templateName,
        usedTenantTemplate: templateSource === "tenant",
      };
    }

    // Handle JSON response (markdown)
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.detail || "Document generation failed");
    }

    // Track usage for document generation
    try {
      const trackResponse = await apiFetch("/api/auth/track-usage", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          usage_type: "document_generation",
          increment: 1,
        }),
      });
      if (trackResponse.ok) {
        console.log("✅ Tracked usage: 1 document generation");
      }
    } catch (error) {
      console.error("Error tracking usage:", error);
      // Don't block the flow if tracking fails
    }

    // Debug: Log the API response
    return {
      ...data.data,
      format: outputFormat,
      isBinary: false,
      templateSource:
        data.data?.template_source || templateSource || null,
      templateName: data.data?.template_name || templateName || null,
      usedTenantTemplate: Boolean(
        data.data?.used_tenant_template ||
          data.data?.template_source === "tenant"
      ),
    };
  } catch (error) {
    console.error("Error generating document:", error);
    throw error;
  }
};

// Export individual functions for convenience
// Export individual functions for backward compatibility
export const {
  // Frontend-used named exports
  getEntities,
  updateEntity,
  deleteEntity,
  createEntity,
  getRelationships,
  updateRelationship,
  deleteRelationship,
  mergeEntities,
  deleteEntityAsync,
  getOperationStatus,
  createEntityAsync,
  createEntitiesBulkHierarchyBackground,
  getJobStatus,
  patchJobEnrichmentReview,
  getEntitySubtypes,
} = api;

// Bound wrappers so callers keep api instance `this` (cache, axios helpers).
export const getHierarchyNodes = (entityType, parentId = null, forceRefresh = false) =>
  api.getHierarchyNodes(entityType, parentId, forceRefresh);
export const invalidateHierarchyNodesCache = (entityType = null) =>
  api.invalidateHierarchyNodesCache(entityType);

// Export session management functions
export { startSessionMonitoring, stopSessionMonitoring };

